import React, { useState } from 'react'
import { Field, useForm, useField } from 'react-final-form'
import InputField from '../input-field'
import styled from '@emotion/styled'
import MultiSelectField from '../multi-select'
import AutoCompleteField from '../autocomplete-field'
import TextAreaField from '../textarea-field'
import CalendarField from '../calendar-field'
import SelectField from '../dropdown-field'
import { gsColors } from '../../../styles/Variables'
import { mq } from '../../../helpers'
import { useAllMarkets } from '../../../services'
import CheckboxSingle from '../../book/steps/components/CheckboxSingle'
import { Text } from '../../../styles/Components'
import { useScrollToFirstError } from '../utils'

const WeddingForm = ({ fieldRefs }) => {
  const form = useForm()
  // Subscribe to the one value this form branches on. `useFormState` with
  // `values: true` would wake this component -- and therefore re-render every
  // Field below it -- on each keystroke in any field.
  const { input: { value: hasDateSet } } = useField('Date Status', {
    subscription: { value: true },
  })
  const markets = useAllMarkets()
  const [showMarketDropdown, setShowMarketDropdown] = useState(false)

  const handleLocationMarketToggle = () => {
    form.change('Address', '')
    setShowMarketDropdown((s) => !s)
  }

  const toggleDateStatus = () => {
    form.change('Date Status', !hasDateSet)
  }

  useScrollToFirstError(form, fieldRefs)

  return (
    <WeddingsFormWrapper>
      <div className="form-inner">
        <div className="detail-section">
          <h2>Contact Details</h2>

          <div className="content">
            <Field
              component={InputField}
              placeholder="First Name"
              name="First Name"
              ref={(el) => (fieldRefs.current['First Name'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Last Name"
              name="Last Name"
              ref={(el) => (fieldRefs.current['Last Name'] = el)}
            />
            <Field
              component={InputField}
              placeholder="Phone Number"
              name="Phone Number"
              parse={(value) => value && value.replace(/[^\d+\-\s()]/g, '')}
              inputMode="tel"
              ref={(el) => (fieldRefs.current['Phone Number'] = el)}
            />
            <Field
              component={InputField}
              ref={(el) => (fieldRefs.current['Email'] = el)}
              placeholder="
              Email"
              name="Email"
            />
          </div>
        </div>

        <div className="person-booking-section">
          <h2>Are you the:</h2>
          <MultiSelectField
            name="Person Booking"
            isSingleSelect={true}
            options={['Bride', 'Wedding Planner', 'Family Member', 'Other']}
            ref={(el) => (fieldRefs.current['Person Booking'] = el)}
          />
        </div>

        <div className="event-detail-section">
          <h2>Event details</h2>
          <MultiSelectField
            name="Services"
            placeholder="Services"
            options={['Hair', 'Makeup']}
            ref={(el) => (fieldRefs.current['Services'] = el)}
            boldTitle={true}
          />
          <MultiSelectField
            name="Type of Services"
            placeholder="Type of Services"
            options={['Full Glam', 'Express/Touch-Up', 'Children']}
            ref={(el) => (fieldRefs.current['Type of Services'] = el)}
            boldTitle={true}
          />

          <div className="location-container">
            <Text>Address</Text>
            <div className="checkbox-container">
              <CheckboxSingle
                textComponent={() => (
                  <Text
                    fontSize={18}
                    lineHeight="120%"
                    spacing="0.36px"
                    margin="0 0 0 20px"
                  >
                    I have the address
                  </Text>
                )}
                name="showMarket"
                value={!showMarketDropdown}
                whiteUnchecked={true}
                onChange={handleLocationMarketToggle}
                cobaltChecked={true}
              />
              <CheckboxSingle
                textComponent={() => (
                  <Text
                    fontSize={18}
                    lineHeight="120%"
                    spacing="0.36px"
                    margin="0 0 0 20px"
                  >
                    I don&apos;t know the address yet
                  </Text>
                )}
                name="showMarket"
                value={showMarketDropdown}
                whiteUnchecked={true}
                onChange={handleLocationMarketToggle}
                cobaltChecked={true}
              />
            </div>
            {!showMarketDropdown && (
              <AutoCompleteField
                name="Address"
                placeholder="Address (where you'll be getting ready)"
                ref={(el) => (fieldRefs.current['Address'] = el)}
              />
            )}

            {showMarketDropdown && !!markets && markets.length && (
              <SelectField
                name="Address"
                placeholder="Location"
                options={markets.map((m) => m.name)}
                ref={(el) => (fieldRefs.current['Address'] = el)}
              />
            )}
          </div>

          <div className='date-status'>
            <div className="checkbox-container">
              <CheckboxSingle
                textComponent={() => (
                  <Text
                    fontSize={18}
                    lineHeight="120%"
                    spacing="0.36px"
                    margin="0 0 0 20px"
                  >
                    I know the date
                  </Text>
                )}
                name="dateStatus"
                value={hasDateSet}
                whiteUnchecked={true}
                onChange={toggleDateStatus}
                cobaltChecked={true}
              />
              <CheckboxSingle
                textComponent={() => (
                  <Text
                    fontSize={18}
                    lineHeight="120%"
                    spacing="0.36px"
                    margin="0 0 0 20px"
                  >
                    I don&apos;t know the date yet
                  </Text>
                )}
                name="dateStatus"
                value={!hasDateSet}
                onChange={toggleDateStatus}
                whiteUnchecked={true}
                cobaltChecked={true}
              />
            </div>
          </div>

          <div className="wedding-info">
            { hasDateSet && (
              <>
                <CalendarField 
                  placeholder="Wedding Date"
                  name="Wedding Date" 
                  ref={(el) => (fieldRefs.current['Wedding Date'] = el)}
                  className='disabled-date'
                  disabled={!hasDateSet}
                />

                <CalendarField
                  placeholder="Trial Date (Optional)"
                  name="Trial Date"
                  className='disabled-date'
                  disabled={!hasDateSet}
                />
              </>
            )}

            <SelectField
              name="Number of People"
              placeholder="Number of people needing services"
              options={[
                { value: '1', name: '1 Person' },
                { value: '2-4', name: '2-4 People' },
                { value: '5-9', name: '5-9 People' },
                { value: '10+', name: '10+ People' },
              ]}
              ref={(el) => (fieldRefs.current['Number of People'] = el)}
            />
          </div>
        </div>

        <div className="booking-notes-section">
          <h2>
            Booking Details&nbsp;
            <span className="optional-tag">(Optional)</span>
          </h2>
          <Field
            name="Booking Notes"
            placeholder="From the bride to bridal party, our bridal-certified makeup artists and hairstylists will work closely with you to create the perfect look."
            component={TextAreaField}
          />
        </div>

        <div className="booking-notes-section">
          <h2>
            Budget <span className="optional-tag">(Optional)</span>
          </h2>
          <Field
            name="Budget"
            placeholder="If you have a budget range in mind, please share it."
            component={InputField}
          />
        </div>

        <div className="planner-detail-section">
          <h2>
            Wedding Planner Details{' '}
            <span className="optional-tag">(Optional)</span>
          </h2>
          <div className="planner-inputs">
            <Field
              component={InputField}
              placeholder="Wedding Planner Name"
              name="Wedding Planner Name"
            />
            <Field
              component={InputField}
              placeholder="Wedding Planner Email"
              name="Wedding Planner Email"
            />
          </div>
        </div>

        <div className="referred-section">
          <h2>
            How did you hear about us?
          </h2>
          <SelectField
            name="Attribution"
            placeholder="Select"
            ref={(el) => (fieldRefs.current['Attribution'] = el)}
            options={[
              'Existing Client',
              'Friend / Referral',
              'Facebook / Instagram',
              'Google',
              'The Knot',
              'Wedding Wire',
              `I don't recall`,
              'Direct Email',
              'Batch',
              'Over the Moon',
            ]}
          />
        </div>
      </div>
    </WeddingsFormWrapper>
  )
}

const WeddingsFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;

  .form-inner {
    max-width: 800px;
    width: 100%;
    margin: 80px 0 50px;
    display: flex;
    flex-direction: column;
    gap: 60px;

    ${mq('max', '800px')} {
      padding: 0 16px;
      margin: 40px 0 50px;
    }

    h2 {
      font-size: 20px;
      margin: 0 0 24px 0;
    }

    .detail-section {
      .content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;

        ${mq('max', '768px')} {
          grid-template-columns: 1fr;
        }

        div {
          margin: 0;
        }

        .phone-field-container {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 8px;

          .verified-badge {
            color: ${gsColors.persianGreen};
            font-size: 14px;
            font-weight: 600;
            align-self: flex-start;
            margin-top: -4px;
          }
        }
      }
    }

    .planner-detail-section {
      .planner-inputs {
        display: flex;
        gap: 32px;

        ${mq('max', '768px')} {
          flex-direction: column;
        }
      }
    }

    .event-detail-section {
      display: flex;
      flex-direction: column;

      .date-status {
        .checkbox-container {
          margin: 24px 0;
          display: flex;
          gap: 24px;

          label {
            display: flex;
            padding-left: 0;

            p {
              font-weight: 600;
              font-size: 18px !important;
              ${mq('max', '768px')} {
                transform: translateY(4px);
                margin-left: 12px;
              }
            }
          }
        }
      }

      .wedding-info {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 32px;
        margin-bottom: 24px;

        ${mq('max', '768px')} {
          grid-template-columns: 1fr;
        }
      }

      .date-known-toggle {
        display: flex;

        p {
          font-weight: 600;
        }
      }
      

      .location-container {
        margin-bottom: 24px;

        p {
          font-size: 18px;
          font-weight: 600;
          line-height: 120%;
          letter-spacing: 0.18px;
        }

        .address-unknown {
          margin-top: 27px;
        }
      }

      .checkbox-container {
        margin: 24px 0;
        display: flex;
        gap: 24px;

        label {
          display: flex;
          padding-left: 0;

          p {
            ${mq('max', '768px')} {
              transform: translateY(4px);
              margin-left: 12px;
            }
          }
        }
      }
    }

    .optional-tag {
      font-weight: 400;
      color: ${gsColors.meredithGrey};
    }
  }
`

export default WeddingForm
